import { useState, useCallback, useRef, useEffect } from "react";
import { UndoRedoManager } from "./undo-redo.manager";
import {
  UndoRedoState,
  UndoRedoConfig,
  UndoRedoCallbacks,
  DeletedRowData,
} from "./undo-redo.types";
import { CellUpdatePayload } from "@modules/spreadsheets/dto/spreadsheet/type";
import { SpreadsheetTableColumnId } from "../table.types";

/**
 * React hook for managing undo/redo functionality
 */
export const useUndoRedo = (
  config: UndoRedoConfig = {},
  callbacks: UndoRedoCallbacks
) => {
  const [state, setState] = useState<UndoRedoState>({
    undoStack: [],
    redoStack: [],
    maxStackSize: config.maxStackSize || 50,
    isPerformingAction: false,
  });

  const managerRef = useRef<UndoRedoManager | null>(null);

  // Initialize manager only once
  useEffect(() => {
    managerRef.current = new UndoRedoManager(config, callbacks);
    setState(managerRef.current.getState());
  }, []); // Empty dependency array - only initialize once

  // Update manager callbacks when they change
  useEffect(() => {
    if (managerRef.current) {
      managerRef.current.updateCallbacks(callbacks);
    }
  }, [callbacks]);

  /**
   * Track a cell edit action
   */
  const trackCellEdit = useCallback(
    (
      currentPayload: CellUpdatePayload,
      rowIndex: number,
      columnId: SpreadsheetTableColumnId,
      previousPayload: CellUpdatePayload,
      popPreviousUndo: boolean = false
    ) => {
      if (managerRef.current) {
        managerRef.current.trackCellEdit(
          currentPayload,
          rowIndex,
          columnId,
          previousPayload,
          popPreviousUndo
        );
        setState(managerRef.current.getState());
      }
    },
    []
  );

  /**
   * Track a row add action
   */
  const trackRowAdd = useCallback(
    (
      rowIndex: number,
      columnId: SpreadsheetTableColumnId,
      globalId: number,
      tableId: number,
      context: number
    ) => {
      if (managerRef.current) {
        managerRef.current.trackRowAdd(
          rowIndex,
          columnId,
          globalId,
          tableId,
          context
        );
        const newState = managerRef.current.getState();
        setState(newState);
      } else {
        console.error("[DEBUG] Manager not initialized in trackRowAdd!");
      }
    },
    []
  );

  /**
   * Track a row delete action (supports both single and bulk operations)
   */
  const trackRowDelete = useCallback(
    (
      deletedRows: Array<{
        rowIndex: number;
        columnId: SpreadsheetTableColumnId;
        globalId: number;
        tableId: number;
        rrn: number;
        deletedRowData: Array<DeletedRowData>;
      }>
    ) => {
      if (managerRef.current) {
        managerRef.current.trackRowDelete(deletedRows);
        const newState = managerRef.current.getState();
        setState(newState);
      } else {
        console.error("[DEBUG] Manager not initialized in trackRowDelete!");
      }
    },
    []
  );

  /**
   * Track a bulk fill action (fill up/down)
   */
  const trackBulkFill = useCallback(
    (
      fillOperations: Array<{
        rowIndex: number;
        columnId: SpreadsheetTableColumnId;
        newValue: string | number;
        originalValue: string | number;
        payload: CellUpdatePayload;
        columnIndex: number;
      }>,
      fillDirection: "up" | "down"
    ) => {
      if (managerRef.current) {
        managerRef.current.trackBulkFill(fillOperations, fillDirection);
        setState(managerRef.current.getState());
      } else {
        console.error("[DEBUG] Manager not initialized in trackBulkFill!");
      }
    }, []);

  /**
   * Undo the last action
   */
  const undo = useCallback(async () => {
    if (managerRef.current) {
      await managerRef.current.undo();
      setState(managerRef.current.getState());
    }
  }, []);

  /**
   * Redo the last undone action
   */
  const redo = useCallback(async () => {
    if (managerRef.current) {
      await managerRef.current.redo();
      setState(managerRef.current.getState());
    }
  }, []);

  /**
   * Clear all undo/redo history
   */
  const clearHistory = useCallback(() => {
    if (managerRef.current) {
      managerRef.current.clearHistory();
      setState(managerRef.current.getState());
    }
  }, []);

  return {
    // State
    state,

    // Actions
    trackCellEdit,
    trackRowAdd,
    trackRowDelete,
    trackBulkFill,
    undo,
    redo,
    clearHistory,

    // Status checks
    canUndo: state.undoStack.length > 0 && !state.isPerformingAction,
    canRedo: state.redoStack.length > 0 && !state.isPerformingAction,
  };
};
